import {AxiosRequestConfig} from 'axios';
import {Invitation, InvitationResponse, OpportunityService} from './opportunity.service';
import Logger from '../../logger';
import {getAuthToken} from '../../utils';
import {APIServiceImpl, ServiceResponse} from '../api';
import {JobApplication, JobApplicationStatus} from '../../models/job-application';
import {CandidateProfile} from '../../models';
import {CreateOpportunityParams, Opportunity} from '../../models/opportunity';
import {RecommendedCandidatesParams, RecommendedCandidatesResponse} from './recommended-candidates-params';
import {OpportunityCreateServiceParams, OpportunitySearchParams} from './opportunity-params';

export class OpportunityServiceImpl extends APIServiceImpl implements OpportunityService {
  constructor() {
    super();
    this.service.interceptors.request.use((request): AxiosRequestConfig => {
      const token = getAuthToken();
      token && (request.headers.Authorization = `Bearer ${token}`);
      return request;
    });
  }

  static readonly APPLY_TO_OPPORTUNITY = '/accounts/:accountId/opportunities/:opportunityId/applications';
  static readonly CREATE_OPPORTUNITY = '/accounts/:accountId/organizations/:organizationId/opportunities';
  static readonly DELETE_OPPORTUNITY = '/accounts/:accountId/organizations/:organizationId/opportunities/:opportunityId';
  static readonly GET_ALL_OPPORTUNITIES = '/opportunities';
  static readonly GET_OPPORTUNITY_BY_ID = '/opportunities/:opportunityId';
  static readonly PATCH_OPPORTUNITY = '/accounts/:accountId/organizations/:organizationId/opportunities/:opportunityId';
  static readonly GET_JOB_APPLICATIONS = '/accounts/:accountId/opportunities/:opportunityId/applications';
  static readonly GET_RECOMMENDED_CANDIDATES = '/accounts/:accountId/opportunities/:opportunityId/recommended-candidates';
  static readonly POST_INVITATIONS = '/accounts/:accountId/opportunities/:opportunityId/invitations';
  static readonly GET_INVITATIONS = '/accounts/:accountId/opportunities/:opportunityId/invitations';
  static readonly UPDATE_APPLICATION_STATUS = '/accounts/:accountId/opportunities/:opportunityId/applications/:applicationId';

  async applyToOpportunity(params: {accountId: string; opportunityId: string}): Promise<ServiceResponse<{success: boolean}>> {
    try {
      const url = OpportunityServiceImpl.APPLY_TO_OPPORTUNITY.replace(':accountId', params.accountId).replace(
        ':opportunityId',
        params.opportunityId,
      );

      const {data: res} = await this.post(url, {});
      return new ServiceResponse<{success: boolean}>(res);
    } catch (e: any) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`POST ${OpportunityServiceImpl.APPLY_TO_OPPORTUNITY}: ${e.toString()}: ${error}`);
      return new ServiceResponse<{success: boolean}>(undefined, error);
    }
  }

  async createOpportunity(params: OpportunityCreateServiceParams): Promise<ServiceResponse<Opportunity>> {
    try {
      const url = OpportunityServiceImpl.CREATE_OPPORTUNITY.replace(':accountId', params.accountId).replace(
        ':organizationId',
        params.organizationId,
      );

      const {data: res} = await this.post(url, params);
      return new ServiceResponse<Opportunity>(res ? new Opportunity(res) : undefined);
    } catch (e: any) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`POST ${OpportunityServiceImpl.CREATE_OPPORTUNITY} ${JSON.stringify(params)}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Opportunity>(undefined, error);
    }
  }

  async deleteOpportunity(accountId: string, opportunityId: string, organizationId: string): Promise<ServiceResponse<void>> {
    try {
      const url = OpportunityServiceImpl.DELETE_OPPORTUNITY.replace(':accountId', accountId)
        .replace(':organizationId', organizationId)
        .replace(':opportunityId', opportunityId);

      await this.delete(url);
      return new ServiceResponse<void>(undefined);
    } catch (e: any) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`DELETE ${OpportunityServiceImpl.DELETE_OPPORTUNITY}: ${e.toString()}: ${error}`);
      return new ServiceResponse<void>(undefined, error);
    }
  }

  async getAllOpportunities(searchParams?: OpportunitySearchParams): Promise<
    ServiceResponse<{
      data: Opportunity[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  > {
    try {
      const params = searchParams
        ? {
            ...searchParams,
            organizationIds: searchParams.organizationIds?.join(','),
          }
        : undefined;

      const config: AxiosRequestConfig = {
        params,
      };

      const {data: res} = await this.get(OpportunityServiceImpl.GET_ALL_OPPORTUNITIES, config);

      if (res && res.data) {
        res.data = res.data.map((opportunity: any) => new Opportunity(opportunity));
      }

      return new ServiceResponse<{
        data: Opportunity[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>(res);
    } catch (e: any) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${OpportunityServiceImpl.GET_ALL_OPPORTUNITIES}: ${e.toString()}: ${error}`);
      return new ServiceResponse<{
        data: Opportunity[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>(undefined, error);
    }
  }

  async getOpportunityById(opportunityId: string): Promise<ServiceResponse<Opportunity>> {
    try {
      const url = OpportunityServiceImpl.GET_OPPORTUNITY_BY_ID.replace(':opportunityId', opportunityId);
      const {data: res} = await this.get(url);
      return new ServiceResponse<Opportunity>(res ? new Opportunity(res) : undefined);
    } catch (e: any) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(
        `GET ${OpportunityServiceImpl.GET_OPPORTUNITY_BY_ID.replace(':opportunityId', opportunityId)}: ${e.toString()}: ${error}`,
      );
      return new ServiceResponse<Opportunity>(undefined, error);
    }
  }

  async patchOpportunity(
    accountId: string,
    opportunityId: string,
    organizationId: string,
    updates: CreateOpportunityParams,
  ): Promise<ServiceResponse<Opportunity>> {
    try {
      const url = OpportunityServiceImpl.PATCH_OPPORTUNITY.replace(':accountId', accountId)
        .replace(':organizationId', organizationId)
        .replace(':opportunityId', opportunityId);

      const {data: res} = await this.patch(url, updates);
      return new ServiceResponse<Opportunity>(res ? new Opportunity(res) : undefined);
    } catch (e: any) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`PATCH ${OpportunityServiceImpl.PATCH_OPPORTUNITY}: ${e.toString()}: ${error}`);
      return new ServiceResponse<Opportunity>(undefined, error);
    }
  }

  async getRecommendedCandidates(
    accountId: string,
    opportunityId: string,
    params?: RecommendedCandidatesParams,
  ): Promise<ServiceResponse<RecommendedCandidatesResponse>> {
    try {
      const url = OpportunityServiceImpl.GET_RECOMMENDED_CANDIDATES.replace(':accountId', accountId).replace(
        ':opportunityId',
        opportunityId,
      );
      const queryParams: Record<string, string | number | undefined> = {
        limit: params?.limit,
        page: params?.page,
        q: params?.q ?? params?.search,
      };
      const config: AxiosRequestConfig = {
        params: Object.fromEntries(Object.entries(queryParams).filter(([, v]) => v !== undefined)),
      };
      const {data: res} = await this.get(url, config);
      if (!res) {
        return new ServiceResponse<RecommendedCandidatesResponse>(undefined, 'No response');
      }
      const data = Array.isArray(res.data)
        ? (res.data as Record<string, unknown>[]).map((entry: Record<string, unknown>) => {
            const rawCandidate = entry.candidate as Record<string, unknown> | undefined;
            const candidate = new CandidateProfile(rawCandidate) as CandidateProfile & {score?: number};
            if (typeof rawCandidate?.score === 'number') candidate.score = rawCandidate.score;
            return {
              application: entry.application
                ? {
                    id: String((entry.application as {id?: string}).id ?? ''),
                    status: (entry.application as {status?: string}).status as JobApplicationStatus,
                  }
                : null,
              candidate,
              inviteEligible: Boolean(entry.inviteEligible),
            };
          })
        : [];
      return new ServiceResponse<RecommendedCandidatesResponse>({
        data,
        hasMore: Boolean(res.hasMore),
        limit: Number(res.limit) ?? params?.limit ?? 10,
        page: Number(res.page) ?? params?.page ?? 1,
        query: res.query,
        resultType: res.resultType,
      });
    } catch (e: unknown) {
      const error =
        (e as {response?: {data?: {message?: string}; status?: number}})?.response?.data?.message ||
        (e as Error).message ||
        String((e as {response?: {status?: number}})?.response?.status ?? '');
      Logger.error(`GET ${OpportunityServiceImpl.GET_RECOMMENDED_CANDIDATES}: ${String(e)}: ${error}`);
      return new ServiceResponse<RecommendedCandidatesResponse>(undefined, error);
    }
  }

  async inviteCandidate(
    accountId: string,
    opportunityId: string,
    candidateAccountId: string,
  ): Promise<ServiceResponse<InvitationResponse>> {
    try {
      const url = OpportunityServiceImpl.POST_INVITATIONS.replace(':accountId', accountId).replace(':opportunityId', opportunityId);
      const {data: res} = await this.post(url, {candidateAccountId});
      return new ServiceResponse<InvitationResponse>(res ?? {});
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}; status?: number}; message?: string};
      const message =
        err.response?.data?.message ||
        err.message ||
        (err.response?.status === 409
          ? 'Candidate already applied or invited.'
          : err.response?.status === 404
          ? 'Opportunity not found.'
          : err.response?.status === 400
          ? 'Missing candidate.'
          : 'Failed to send invitation.');
      Logger.error(`POST ${OpportunityServiceImpl.POST_INVITATIONS}: ${String(e)}: ${message}`);
      return new ServiceResponse<InvitationResponse>(undefined, message);
    }
  }

  async listInvitations(
    accountId: string,
    opportunityId: string,
    candidateAccountId?: string,
  ): Promise<ServiceResponse<{data: Invitation[]}>> {
    try {
      const url = OpportunityServiceImpl.GET_INVITATIONS.replace(':accountId', accountId).replace(':opportunityId', opportunityId);
      const config: AxiosRequestConfig = {
        params: candidateAccountId ? {accountId: candidateAccountId} : undefined,
      };
      const {data: res} = await this.get(url, config);
      return new ServiceResponse<{data: Invitation[]}>(res ?? {data: []});
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}; status?: number}; message?: string};
      const message = err.response?.data?.message || err.message || 'Failed to load invitations.';
      Logger.error(`GET ${OpportunityServiceImpl.GET_INVITATIONS}: ${String(e)}: ${message}`);
      return new ServiceResponse<{data: Invitation[]}>(undefined, message);
    }
  }

  async getJobApplications(
    accountId: string,
    opportunityId: string,
    params?: {page?: number; limit?: number; status?: string; search?: string},
  ): Promise<
    ServiceResponse<{
      data: JobApplication[];
      total: number;
      hasMore: boolean;
      page: number;
      totalPages: number;
    }>
  > {
    try {
      const url = OpportunityServiceImpl.GET_JOB_APPLICATIONS.replace(':accountId', accountId).replace(':opportunityId', opportunityId);

      const config: AxiosRequestConfig = {params};

      const {data: res} = await this.get(url, config);

      const {data, total, page = 1, totalPages = 1} = res;

      const mappedData = data.map((app: any) => new JobApplication(app));

      const hasMore = page < totalPages;

      return new ServiceResponse({
        data: mappedData,
        total,
        hasMore,
        page,
        totalPages,
      });
    } catch (e: any) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(
        `GET ${OpportunityServiceImpl.GET_JOB_APPLICATIONS.replace(':opportunityId', opportunityId)}: ${e.toString()}: ${error}`,
      );

      return new ServiceResponse<{data: JobApplication[]; total: number; hasMore: boolean; page: number; totalPages: number}>(
        undefined,
        error,
      );
    }
  }

  async updateApplicationStatus(
    accountId: string,
    opportunityId: string,
    applicationId: string,
    status: JobApplicationStatus,
  ): Promise<ServiceResponse<JobApplication>> {
    try {
      const url = OpportunityServiceImpl.UPDATE_APPLICATION_STATUS.replace(':accountId', accountId)
        .replace(':opportunityId', opportunityId)
        .replace(':applicationId', applicationId);

      const {data: res} = await this.patch(url, {status});

      return new ServiceResponse<JobApplication>(res ? new JobApplication(res) : undefined);
    } catch (e: any) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`PATCH ${OpportunityServiceImpl.UPDATE_APPLICATION_STATUS}: ${e.toString()}: ${error}`);
      return new ServiceResponse<JobApplication>(undefined, error);
    }
  }
}
